import { currentViewport, DEVICES } from './breakpoint';
import { setOverflowBody, setOverflowElement } from './header-helpers';

interface HeaderAnimationConstructorModel {
  header: HTMLElement | null;
  burgerButton?: HTMLButtonElement | null;
  closeButton?: HTMLButtonElement | null;
  container?: HTMLDivElement | null;
  navigation?: HTMLDivElement | null;
  topBarDesktop?: HTMLDivElement | null;
  topBarMobile?: HTMLDivElement | null;
  navigationItems?: NodeListOf<HTMLDivElement> | null;
}

interface MobileSubNavOpenDetailModel {
  target: HTMLElement;
}

class HeaderAnimation {
  header: HTMLElement | null;
  burgerButton?: HTMLButtonElement | null;
  closeButton?: HTMLButtonElement | null;
  container?: HTMLDivElement | null;
  navigation?: HTMLDivElement | null;
  topBarDesktop?: HTMLDivElement | null;
  topBarMobile?: HTMLDivElement | null;
  navigationItems?: NodeListOf<HTMLDivElement> | null;

  constructor({
    header,
    burgerButton,
    closeButton,
    container,
    navigation,
    topBarDesktop,
    topBarMobile,
    navigationItems,
  }: HeaderAnimationConstructorModel) {
    this.header = header;
    this.burgerButton = burgerButton;
    this.closeButton = closeButton;
    this.container = container;
    this.navigation = navigation;
    this.topBarDesktop = topBarDesktop;
    this.topBarMobile = topBarMobile;
    this.navigationItems = navigationItems;
  }

  init() {
    this.listenAnimationChangeOnHeader();
    this.listenHeaderEvent();
    this.setCssVariable();
  }

  setCssVariable() {
    this.setHeaderCssVariable();
    this.setMobileNavItemNumberCssVariable();
  }

  setMobileNavItemNumberCssVariable() {
    // navigation items
    [].forEach.call(this.navigationItems, (navItem: HTMLElement, index: number) => {
      navItem.style.setProperty('--mobile-nav-item-number', `${index + 1}`);
    });

    // remaining items
    const remainingItems = this.topBarMobile?.querySelectorAll<HTMLElement>(
      '.kf-o-top-bar__dropdown, .kf-o-top-bar__links .kf-a-link, .kf-o-top-bar__my-kf-button'
    );

    [].forEach.call(remainingItems, (item: HTMLElement, index: number) => {
      item.style.setProperty('--mobile-nav-item-number', `${(this.navigationItems?.length || 0) + index + 1}`);
    });
  }

  listenHeaderEvent() {
    this.header?.addEventListener('mobile-sub-nav-opened', (event: Event) => {
      const { target } = (event as CustomEvent<MobileSubNavOpenDetailModel>).detail;

      this.setClassAnimationSubmenuMobile(target, true, false);
    });

    this.header?.addEventListener('mobile-sub-nav-close', () => {
      if (this.header) {
        this.setClassAnimationSubmenuMobile(this.header, false, false);
      }
    });

    this.header?.addEventListener('idle-state', () => {
      this.removeHeaderMobileAnimationClass();
    });

    this.header?.addEventListener('main-nav-state', () => {
      this.setStartHeightOpenedMenu();

      // let run animation at the next loop
      window.setTimeout(this.setHeaderMobileAnimationClass.bind(this), 0);
    });

    this.header?.addEventListener('header-change-height-event', this.setHeaderCssVariable.bind(this));
  }

  setHeaderCssVariable() {
    if (currentViewport() === DEVICES.DESKTOP) return;

    document.documentElement.style.setProperty('--header-initial-height', `${this.header?.offsetHeight || 0}px`);
  }

  setStartHeightOpenedMenu() {
    if (currentViewport() === DEVICES.DESKTOP) return;

    const leftSide = this.container?.querySelector<HTMLElement>('.kf-o-header__left-side');
    const height = (leftSide?.offsetHeight || 0) + (this.container?.offsetTop || 0);
    const borderBottomWidth = parseFloat(window.getComputedStyle(this.header as HTMLElement).borderBottomWidth || '0') || 0;

    this.header?.style.setProperty('--header-start-height', `${height + borderBottomWidth}px`);
  }

  setHeaderMobileAnimationClass() {
    if (currentViewport() === DEVICES.DESKTOP) return;

    this.header?.classList.add('run-animation-mobile');
  }

  removeHeaderMobileAnimationClass() {
    if (currentViewport() === DEVICES.DESKTOP) return;

    // preprocess before run animation
    setOverflowElement(this.header, true);
    setOverflowBody(false);

    // run
    this.header?.classList.remove('run-animation-mobile');
  }

  listenAnimationChangeOnHeader() {
    [this.topBarDesktop, this.container].forEach((elementHoverAnimation) => {
      this.listenMouseEnterEvent(elementHoverAnimation);
      this.listenMouseLeaveEvent(elementHoverAnimation);
      this.listenTransitionEndEvent();
      this.listenAnimationEndEvent();
    });
  }

  listenAnimationEndEvent() {
    this.header?.addEventListener('animationend', (event: AnimationEvent) => {
      event.stopPropagation();

      if (currentViewport() === DEVICES.DESKTOP || !this.header) {
        return;
      }

      if (event.animationName === 'globalnav-flyout-slide-forward-next') {
        // let wait a little bit before remove class
        setTimeout(() => {
          if (this.header) {
            this.setClassAnimationSubmenuMobile(this.header, true, true);
          }
        }, 100);

        return;
      }

      if (event.animationName === 'globalnav-flyout-slide-back-previous') {
        // let wait a little bit before remove class
        setTimeout(() => {
          if (this.header) {
            this.setClassAnimationSubmenuMobile(this.header, false, true);
            this.header.dispatchEvent(new CustomEvent('close-mobile-sub-nav'));
          }
        }, 100);
      }
    });
  }

  listenTransitionEndEvent() {
    this.header?.addEventListener('transitionend', (event: TransitionEvent) => {
      if (
        (currentViewport() === DEVICES.DESKTOP && event.propertyName) === 'background-color' &&
        !this.header?.classList.contains('hover-animation')
      ) {
        this.header?.classList.remove('hide-none-animation');

        return;
      }

      // after height animation finish, close main nav and back to main state. Only trigger on mobile
      if (
        currentViewport() !== DEVICES.DESKTOP &&
        event.propertyName === 'height' &&
        this.header?.classList.contains('kf-o-header--mobile-opened') &&
        !this.header.classList.contains('run-animation-mobile')
      ) {
        this.header.dispatchEvent(new CustomEvent('close-mobile-main-nav'));

        setOverflowElement(this.header, false);
      }
    });
  }

  listenMouseEnterEvent(element?: HTMLElement | null) {
    element?.addEventListener('mouseenter', () => {
      if (currentViewport() !== DEVICES.DESKTOP) {
        return;
      }

      this.header?.classList.add('hover-animation');
      this.header?.classList.add('hide-none-animation');
    });
  }

  listenMouseLeaveEvent(element?: HTMLElement | null) {
    element?.addEventListener('mouseleave', () => {
      if (currentViewport() !== DEVICES.DESKTOP) {
        return;
      }

      this.header?.classList.remove('hover-animation');
    });
  }

  setClassAnimationSubmenuMobile(target: HTMLElement, isOpen: boolean, removeClass: boolean) {
    if (currentViewport() === DEVICES.DESKTOP) return;

    const headerClass = isOpen ? 'submenu-opening-mobile' : 'submenu-closing-mobile';

    if (!removeClass) {
      this.header?.classList.add(headerClass);
    } else {
      this.header?.classList.remove(headerClass);
    }

    const submenuClass = isOpen ? 'slide-forward-next' : 'slide-back-previous';

    this.setParentItemAnimationClassMobile(isOpen, removeClass);

    // submenu flyout
    const subHeader = target?.querySelectorAll<HTMLElement>('.kf-o-header__sub-header');

    [].forEach.call(subHeader, (item: HTMLElement) => {
      if (!removeClass) {
        item?.classList.add(submenuClass);
      } else {
        item?.classList.remove(submenuClass);
      }
    });
  }

  setParentItemAnimationClassMobile(isOpen: boolean, removeClass: boolean) {
    if (currentViewport() === DEVICES.DESKTOP) return;

    const itemClass = isOpen ? 'slide-forward-previous' : 'slide-back-next';

    // parent navigation items
    [].forEach.call(this.navigationItems, (navItem: HTMLElement) => {
      if (!removeClass) {
        navItem.classList.add(itemClass);
      } else {
        navItem.classList.remove(itemClass);
      }
    });

    // parent top bar items
    const topBarItems = this.topBarMobile?.querySelectorAll<HTMLElement>(
      '.kf-o-top-bar__dropdown, .kf-o-top-bar__links .kf-a-link, .kf-o-top-bar__my-kf-button'
    );

    [].forEach.call(topBarItems, (item: HTMLElement) => {
      if (!removeClass) {
        item.classList.add(itemClass);
      } else {
        item.classList.remove(itemClass);
      }
    });

    // parent left side
    const leftSide = this.header?.querySelector<HTMLElement>('.kf-o-header__left-side');

    // country selector
    const countrySelector = this.header?.querySelector<HTMLElement>('.kf-o-country-selector-prompt');

    if (!removeClass) {
      leftSide?.classList.add(itemClass);
      countrySelector?.classList.add(itemClass);
    } else {
      leftSide?.classList.remove(itemClass);
      countrySelector?.classList.remove(itemClass);
    }
  }
}

export default HeaderAnimation;
